import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { maskCpfAndCnpj, maskOdometer, maskPhoneNumber } from 'src/assets/functions/information-masks';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ChoosePlanBuyng } from '../../models/choose-plan/choose-plan-buyng';
import { ClientData } from '../../models/client-data/client-data';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { Patterns } from '../../models/constants/patterns';
import { ContractSummary } from '../../models/contract-summary/contract-summary';
import { ContractPendingSignature } from '../../models/contract/contract-pending-signature';
import { Dealer } from '../../models/dealer/dealer';
import { DealerApsFordDetailAttributes } from '../../models/dealer/dealer-aps-ford-detail-attributes';
import { DealerSeller } from '../../models/dealer/dealer-seller';
import { OperationCode } from '../../models/enum/operation-code';
import { SaleChannel } from '../../models/enum/sale-channel';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { StatusChassi } from '../../models/enum/status-chassi';
import { StatusTypeEnum } from '../../models/enum/status-type';
import { ProductFull } from '../../models/product/product-full';
import { VinValidate } from "../../models/vin-validate/vin-validate";
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../services/choose-plan/choose-plan.service';
import { ClientDataService } from '../../services/client-data/client-data.service';
import { ContractSignatureDraftService } from '../../services/contract/contract-signature-draft.service';
import { ContractSummaryService } from '../../services/contract-summary/contract-summary.service';
import { DealerService } from '../../services/dealer/dealer.service';
import { ValidationErrorMapperService } from '../../services/address/validation-error-mapper.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';
import { nationalId } from 'src/assets/validations/cpf_cnpj_validation';

@Component({
	selector: 'app-customer',
	templateUrl: './contract-summary.component.html',
	styleUrls: ['./contract-summary.component.scss'],
	standalone: false
})
export class ContractSummaryComponent implements OnInit {
	userform: UntypedFormGroup;
	vinValidate: VinValidate = new VinValidate();
	clientData: ClientData = new ClientData();
	contractSummary: ContractSummary;
	choosePlanBuyng: ChoosePlanBuyng;
	cashPayment = ConstantsFordProtect.CASH_PAYMENT;
	paymentInstallment = ConstantsFordProtect.PAYMENT_INSTALLMENT;
	disableFormaDropdown: boolean = false;
	cdsid = sessionStorage.getItem('userId') || '';
	saleChannel!: SaleChannel;
	nextStepText: string = '';
	unityPrice = ConstantsFordProtect.PESO || 'R$';

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dropdownTypePayment: any[] = [
		{ label: 'TRANSLATE.YES', value: true },
		{ label: 'TRANSLATE.NOT', value: false }
	];

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dropdownFormaPayment: any[] = [
		{ label: 'TRANSLATE.IN.CASH', value: this.cashPayment },
		{ label: 'TRANSLATE.INSTALLMENTS', value: this.paymentInstallment }
	];

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dropdownPrazoPayment: any[] = [];

	blockedUi = false;
	contractIdDownload = 0;
	isContractCreated = false;
	lang: string = '';
	doubleZero = ConstantsFordProtect.DOUBLE_ZERO;
	systemCountry: string = '';
	phoneMinLength = 10;
	phoneMaxLength = 11;

	phoneValidationRules: { [key: string]: { minlength: number; maxlength: number } } = {
		'ARG': { 'minlength': 0, 'maxlength': 20 },
		'BRA': { 'minlength': 10, 'maxlength': 11 },
		'CHL': { 'minlength': 11, 'maxlength': 11 },
		'COL': { 'minlength': 7, 'maxlength': 12 },
	}
	readonly maskCpfAndCnpj = maskCpfAndCnpj;
	readonly maskOdometer = maskOdometer;
	readonly maskPhoneNumber = maskPhoneNumber;

	selectedPaymentType: boolean | null = null;
	paymentType: boolean | null = null;
	paymentMethod: string | null = null;
	paymentTerm: number | null = null;
	isDealerCodeValid = false;
	private sellerLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;
	private readonly sellerLookupDebounceMs = 250;
	private dealerLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;
	private readonly dealerLookupDebounceMs = 250;
	isCtaSuccess = false;
	submitted = false;
	private formChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(private contractSummaryService: ContractSummaryService,
		private vehicleInformationService: VehicleInformationService,
		private clientDataService: ClientDataService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private route: ActivatedRoute,
		private fb: UntypedFormBuilder,
		private choosePlanService: ChoosePlanService,
		private apsPermissionService: ApsPermissionService,
		private dealerService: DealerService,
		private contractSignatureDraftService: ContractSignatureDraftService,
		private validationErrorMapperService: ValidationErrorMapperService,
		private systemCountryService: SystemCountryService,
		private readonly destroyRef: DestroyRef
	) {

		this.contractSummary = new ContractSummary();
		this.choosePlanBuyng = new ChoosePlanBuyng();
		this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;

		this.systemCountryService.setSystemCountry();
		this.userform = this.formControlGenerator();
	}

	ngOnInit() {

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.setDealerCodeValidationState(false);

		if (this.systemCountry === ConstantsFordProtect.BRA ||
			this.systemCountry === ConstantsFordProtect.ARG) {

			this.nextStepText = "TRANSLATE.ADVANCE";
		} else {
			this.nextStepText = "TRANSLATE.FINALIZE";
		}

		this.vinValidate = this.vehicleInformationService.getAllChassiValidation();
		this.clientData = this.clientDataService.getClientData();

		if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid ||
			this.clientDataService.getClientData() === null || this.clientData === null ||
			this.choosePlanService.getChoosePlanBuyng() === null) {
			this.router.navigate(['sales-channel', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
			return;
		}

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractReview);

		if (this.contractSummaryService.getContractSummary() !== null) {
			this.contractSummary = this.contractSummaryService.getContractSummary();
		}

		if (this.choosePlanService.getChoosePlanBuyng() !== null) {

			this.choosePlanBuyng = this.choosePlanService.getChoosePlanBuyng();

			if (this.choosePlanBuyng?.extension?.unityPrice) {
				this.unityPrice = this.choosePlanBuyng.extension.unityPrice;
			}
			if (this.choosePlanBuyng?.maintenance?.unityPrice) {
				this.unityPrice = this.choosePlanBuyng.maintenance.unityPrice;
			}
			if (this.choosePlanBuyng?.part?.unityPrice) {
				this.unityPrice = this.choosePlanBuyng.part.unityPrice;
			}
		}

		this.updatePhoneValidation(this.systemCountry);

		// Attempt to restore modified dealer data from previous session
		const hasRestoredSnapshot = this.contractSummaryService.restoreDealerDataSnapshot();

		// If user confirmed data before (navigated forward), restore saved state without running auto-lookups.
		// Auto-lookups would overwrite user modifications with fresh API data.
		if (this.contractSummaryService.isDealerDataConfirmed()) {
			this.setDealerCodeValidationState(!!this.contractSummary.dealer?.legalName);
			this.syncFormFromContractSummary();
			this.syncSelectedPaymentTypeFromSummary();
			this.updateForma();
			return;
		}

		// If we restored a snapshot from returning user, skip auto-lookups
		if (hasRestoredSnapshot) {
			this.setDealerCodeValidationState(!!this.contractSummary.dealer?.legalName);
			this.syncFormFromContractSummary();
			this.syncSelectedPaymentTypeFromSummary();
			this.updateForma();
			return;
		}

		this.getDealerAutomatic();
		this.getSellerAutomatic();

		this.syncFormFromContractSummary();
		this.syncSelectedPaymentTypeFromSummary();
		this.setDealerCodeValidationState(!!this.contractSummary.dealer?.legalName);
		this.updateForma();

		// Listen to form changes for CTA success state
		this.userform.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.onFormChange());
	}

	get isFormComplete(): boolean {
		return this.userform?.valid && this.isDealerCodeValid;
	}

	onFormChange() {
		if (this.formChangeDebounceTimer) {
			clearTimeout(this.formChangeDebounceTimer);
		}
		this.isCtaSuccess = false;

		this.formChangeDebounceTimer = setTimeout(() => {
			this.isCtaSuccess = this.isFormComplete;
		}, 700);
	}

	private setDealerCodeValidationState(isValid: boolean): void {
		this.isDealerCodeValid = isValid;

		const dealerEmailControl = this.userform.get('dealerEmail');
		if (!dealerEmailControl) {
			return;
		}

		if (isValid) {
			dealerEmailControl.enable({ emitEvent: false });
			return;
		}

		dealerEmailControl.reset('', { emitEvent: false });
		dealerEmailControl.disable({ emitEvent: false });

		if (this.contractSummary?.dealer) {
			this.contractSummary.dealer.dealerEmail = '';
		}
	}
	
	private syncFormFromContractSummary(): void {

		if (!this.userform || !this.contractSummary) {
			return;
		}

		const typePaymentValue = this.contractSummary.typePayment && typeof this.contractSummary.typePayment === 'object'
			? this.contractSummary.typePayment
			: this.dropdownTypePayment.find(option => option.value === this.contractSummary.typePayment) || null;

		this.userform.patchValue({
			dealerCodigo: this.contractSummary.dealer?.pandAC || '',
			cdsidSeller: this.contractSummary.dealerSeller?.sellerCode || '',
			nameSeller: this.contractSummary.dealerSeller?.name || '',
			inputPhoneSeller: this.contractSummary.dealerSeller?.phone || '',
			emailSeller: this.contractSummary.dealerSeller?.email || '',
			dealerEmail: this.contractSummary.dealer?.dealerEmail || '',
			typePayment: typePaymentValue,
			formaPayment: this.getDropdownValue(this.contractSummary.formaPayment),
			prazoPayment: this.getDropdownValue(this.contractSummary.prazoPayment)
		}, { emitEvent: false });
	}

	private syncContractSummaryFromForm(): void {

		if (!this.contractSummary) {
			this.contractSummary = new ContractSummary();
		}

		if (!this.contractSummary.dealer) {
			this.contractSummary.dealer = new Dealer();
		}

		if (!this.contractSummary.dealerSeller) {
			this.contractSummary.dealerSeller = new DealerSeller();
		}

		this.contractSummary.dealer.pandAC = this.userform.get('dealerCodigo')?.value || '';
		this.contractSummary.dealer.dealerEmail = this.userform.get('dealerEmail')?.value || '';
		this.contractSummary.dealerSeller.sellerCode = this.userform.get('cdsidSeller')?.value || '';
		this.contractSummary.dealerSeller.name = this.userform.get('nameSeller')?.value || '';
		this.contractSummary.dealerSeller.phone = this.userform.get('inputPhoneSeller')?.value || '';
		this.contractSummary.dealerSeller.email = this.userform.get('emailSeller')?.value || '';

		const selectedTypePayment = this.userform.get('typePayment')?.value;
		if (selectedTypePayment && typeof selectedTypePayment === 'object') {
			this.contractSummary.typePayment = selectedTypePayment;
		} else {
			this.contractSummary.typePayment = this.dropdownTypePayment.find(option => option.value === selectedTypePayment) || null;
		}

		const selectedFormaPayment = this.getDropdownValue<string>(this.userform.get('formaPayment')?.value);
		const selectedPrazoPayment = this.getDropdownValue<number>(this.userform.get('prazoPayment')?.value);

		const formaPaymentOption = this.dropdownFormaPayment.find(option => option.value === selectedFormaPayment);
		const prazoPaymentOption = this.dropdownPrazoPayment.find(option => option.value === selectedPrazoPayment);

		this.contractSummary.formaPayment = selectedFormaPayment === null || selectedFormaPayment === undefined
			? (null as unknown as typeof this.contractSummary.formaPayment)
			: { label: formaPaymentOption?.label || '', value: selectedFormaPayment };

		this.contractSummary.prazoPayment = selectedPrazoPayment === null || selectedPrazoPayment === undefined
			? (null as unknown as typeof this.contractSummary.prazoPayment)
			: { label: prazoPaymentOption?.label || '', value: selectedPrazoPayment };
	}

	updatePhoneValidation(country: string) {

		const validationRules = this.phoneValidationRules[country] || this.phoneValidationRules[ConstantsFordProtect.BRA];

		this.phoneMinLength = validationRules.minlength;
		this.phoneMaxLength = validationRules.maxlength;

		const phoneValidators = [
			Validators.required,
			Validators.minLength(this.phoneMinLength),
			Validators.maxLength(this.phoneMaxLength),
			Validators.pattern(Patterns.ONLY_NUMBERS)
		];

		const phoneSellerFormControl = new UntypedFormControl('', phoneValidators);

		this.userform.setControl('inputPhoneSeller', phoneSellerFormControl);
	}

	paymentTypeOnChange(_event: unknown) {
		this.handlePaymentTypeDraftCleanup();
		this.updateForma();
	}

	private syncSelectedPaymentTypeFromSummary(): void {
		this.selectedPaymentType = this.getDropdownValue<boolean>(this.contractSummary?.typePayment);
	}

	private handlePaymentTypeDraftCleanup(): void {
		const nextPaymentType = this.getDropdownValue<boolean>(this.userform.get('typePayment')?.value);

		if (nextPaymentType === null || nextPaymentType === undefined || this.selectedPaymentType === nextPaymentType) {
			this.selectedPaymentType = nextPaymentType;
			return;
		}

		const previousDraftKey = this.selectedPaymentType
			? this.contractSignatureDraftService.buildAttachmentDraftKey('attachment-one', this.saleChannel, this.vinValidate?.vin || '')
			: this.contractSignatureDraftService.buildAttachmentDraftKey('attachment-two', this.saleChannel, this.vinValidate?.vin || '');

		this.contractSignatureDraftService.clearDraft(previousDraftKey);
		this.selectedPaymentType = nextPaymentType;
	}

	private formControlGenerator() {

		return this.fb.group({
			'dealerCodigo': new UntypedFormControl('', Validators.required),
			'cdsidSeller': new UntypedFormControl('', Validators.required),
			'nameSeller': new UntypedFormControl('', Validators.required),
			'inputPhoneSeller': new UntypedFormControl('', [Validators.pattern(Patterns.ONLY_NUMBERS)]),
			'emailSeller': new UntypedFormControl('', [Validators.required, Validators.pattern(Patterns.EMAIL_STRICT)]),
			'dealerEmail': new UntypedFormControl({ value: '', disabled: true }, [Validators.required, Validators.pattern(Patterns.EMAIL_STRICT)]),
			'typePayment': new UntypedFormControl('', Validators.required),
			'formaPayment': new UntypedFormControl('', Validators.required),
			'prazoPayment': new UntypedFormControl('', Validators.required),
		});
	}

	buttonComeBack() {
		this.syncContractSummaryFromForm();
		this.contractSummaryService.saveDealerDataSnapshot();

		this.dropdownTypePayment = [
			{ label: 'TRANSLATE.YES', value: true },
			{ label: 'TRANSLATE.NOT', value: false }
		];

		this.dropdownFormaPayment = [
			{ label: 'TRANSLATE.IN.CASH', value: this.cashPayment },
			{ label: 'TRANSLATE.INSTALLMENTS', value: this.paymentInstallment }
		];

		this.dropdownPrazoPayment = [];
		this.router.navigate(['client-data', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
	}

	getDealer(_event: Event) {
		if (this.dealerLookupDebounceHandle) {
			clearTimeout(this.dealerLookupDebounceHandle);
		}

		this.dealerLookupDebounceHandle = setTimeout(() => {
			this.dealerLookupDebounceHandle = null;
			this.runDealerLookup();
		}, this.dealerLookupDebounceMs);
	}

	private runDealerLookup(): void {
		this.syncContractSummaryFromForm();
		const searchedDealerCode = (this.contractSummary.dealer?.pandAC || '').trim().toUpperCase();

		if (!searchedDealerCode) {
			this.setDealerCodeValidationState(false);
			return;
		}

		this.contractSummary.dealer.pandAC = searchedDealerCode;
		this.contractSummary.dealer.geoSalesCode = this.systemCountry;
		this.contractSummary.dealer.legalName = '';
		this.contractSummary.dealer.dealerEmail = '';
		this.setDealerCodeValidationState(false);
		this.syncFormFromContractSummary();

		this.dealerService.getDealer(this.contractSummary.dealer)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: Dealer) => {

				if (response) {
					this.contractSummary.dealer = response;
					this.setDealerCodeValidationState(true);
					this.syncFormFromContractSummary();
					this.contractSummaryService.setContractSummary(this.contractSummary);
				} else {
					this.handleDealerNotFound(searchedDealerCode);
				}

			}, (err) => {

				if (err.status === 404) {
					this.handleDealerNotFound(searchedDealerCode);
				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

			});
	}

	private handleDealerNotFound(searchedDealerCode: string): void {
		this.contractSummary.dealer = new Dealer();
		this.contractSummary.dealer.pandAC = searchedDealerCode;
		this.contractSummary.dealer.geoSalesCode = this.systemCountry;
		this.setDealerCodeValidationState(false);
		this.syncFormFromContractSummary();
		this.contractSummaryService.setContractSummary(this.contractSummary);

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.DEALER.NOT.FOUND.ACTION')
		});

		setTimeout(() => {
			(document.getElementById('dealerCodigo') as HTMLInputElement | null)?.focus();
		}, 0);
	}

	getDealerSeller(_event: Event) {
		if (this.sellerLookupDebounceHandle) {
			clearTimeout(this.sellerLookupDebounceHandle);
		}

		this.sellerLookupDebounceHandle = setTimeout(() => {
			this.sellerLookupDebounceHandle = null;
			this.runDealerSellerLookup();
		}, this.sellerLookupDebounceMs);
	}

	private runDealerSellerLookup(): void {
		this.syncContractSummaryFromForm();
		const searchedSellerCode = (this.contractSummary.dealerSeller?.sellerCode || '').trim().toUpperCase();

		if (!searchedSellerCode) {
			return;
		}

		this.contractSummary.dealerSeller.sellerCode = searchedSellerCode;
		this.syncFormFromContractSummary();

		this.dealerService.getDealerSeller(this.contractSummary.dealerSeller)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: DealerSeller | null | undefined) => {

				if (response) {

					this.contractSummary.dealerSeller = response;
					let processedPhone = '';

					if (response.phone) {

						processedPhone = response.phone.replace(/[\s()]/g, '');

						if (processedPhone === ConstantsFordProtect.EMPTY_PHONE_ARGENTINA) {
							processedPhone = '';
						}
					}

					this.contractSummary.dealerSeller.phone = processedPhone;
					this.syncFormFromContractSummary();
					this.contractSummaryService.setContractSummary(this.contractSummary);

				} else {
					this.handleSellerNotFound(searchedSellerCode);
				}

			}, (err) => {

				if (err.status === 404) {
					/* Dealer not found:
					A regra de negocio diz que se o vendedor não for encontrado(404), deve ser possível continuar o fluxo.
					*/
					this.handleSellerNotFound(searchedSellerCode);
				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

			});
	}

	private handleSellerNotFound(searchedSellerCode: string): void {
		this.contractSummary.dealerSeller = new DealerSeller();
		this.contractSummary.dealerSeller.sellerCode = searchedSellerCode;
		this.syncFormFromContractSummary();
		this.contractSummaryService.setContractSummary(this.contractSummary);

		this.messageService.add({
			severity: 'warn',
			summary: this.translateService.instant('TRANSLATE.ATTENTION'),
			detail: this.translateService.instant('TRANSLATE.SELLER.NOT.FOUND.ACTION')
		});

		setTimeout(() => {
			(document.getElementById('phoneSeller') as HTMLInputElement | null)?.focus();
		}, 0);
	}

	getSellerAutomatic() {

		this.contractSummary.dealerSeller.sellerCode = this.cdsid;
		this.dealerService.getDealerSeller(this.contractSummary.dealerSeller)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: DealerSeller) => {

				this.contractSummary.dealerSeller = response;
				this.contractSummary.dealerSeller.id = response.id;
				this.contractSummary.dealerSeller.sellerCode = response.sellerCode;
				this.contractSummary.dealerSeller.name = response.name;
				this.contractSummary.dealerSeller.email = response.email;
				this.contractSummary.dealerSeller.document = response.document;

				let processedPhone = '';

				if (response.phone) {

					processedPhone = response.phone.replace(/[\s()]/g, '');

					if (processedPhone === ConstantsFordProtect.EMPTY_PHONE_ARGENTINA) {
						processedPhone = '';
					}
				}

				this.contractSummary.dealerSeller.phone = processedPhone;
				this.syncFormFromContractSummary();

				if (response.birthday !== null) {
					this.contractSummary.dealerSeller.birthday = new Date(response.birthday);
				}

				this.syncFormFromContractSummary();

			}, (err) => {

				if (err.status === 404) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.SELLER.NOT.FOUND')
					});
				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

			});

	}

	/*
	* captura o cdsid que está na sessão;
	* informa ele no serviço de DLS;
	* se houver um response, verifica se tem um sitecode e atribui;
	* se não houver response, atribui o site code 00100 (FORD MOTOR COMPANY BRASIL LTDA) automaticamente;
	*/
	getDealerAutomatic() {

		this.contractSummary.dealerSeller.name = this.cdsid;
		this.dealerService.getDealerName(this.contractSummary.dealerSeller)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: DealerApsFordDetailAttributes) => {
				if (response === null) {
					this.contractSummary.dealer.pandAC = ConstantsFordProtect.PANDAC_BRA_DEFAULT;
					if (this.systemCountry === ConstantsFordProtect.ARG) {
						this.contractSummary.dealer.pandAC = ConstantsFordProtect.PANDAC_ARG_DEFAULT;
					}
				} else {
					this.contractSummary.dealer.pandAC = response.attributeValue;
				}
				this.contractSummary.dealer.geoSalesCode = this.systemCountry;
				this.dealerService.getDealer(this.contractSummary.dealer)
					.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: Dealer) => {
						if (response) {
							this.contractSummary.dealer = response;
							this.setDealerCodeValidationState(true);
							this.syncFormFromContractSummary();
						} else {
							this.handleDealerNotFound(this.contractSummary.dealer.pandAC || '');
						}
					});
			});
	}

	updateForma() {
		this.syncContractSummaryFromForm();

		this.dropdownPrazoPaymentSelecione();

		const formaPaymentValue = this.getDropdownValue(this.contractSummary?.formaPayment);

		if (formaPaymentValue === null || formaPaymentValue === undefined) {
			return;
		}

		if (formaPaymentValue === this.cashPayment) {
			this.dropdownPrazoPaymentOneMonth();
		} else {
			this.dropdownPrazoPaymentSomeMonths();
		}
	}

	getCurrencyCodeByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
				return 'ARS';
			case ConstantsFordProtect.CHL:
				return 'CLP';
			case ConstantsFordProtect.COL:
				return 'COP';
			default:
				return 'BRL';
		}
	}

	getCurrencyDisplayByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
			case ConstantsFordProtect.CHL:
			case ConstantsFordProtect.COL:
				return '$ ';
			default:
				return 'symbol';
		}
	}

	getLocaleByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
			case ConstantsFordProtect.CHL:
			case ConstantsFordProtect.COL:
				return 'en-US';
			default:
				return ConstantsFordProtect.PT_BR;
		}
	}

	private getDropdownValue<T = unknown>(selection: unknown): T | null {
		if (selection === null || selection === undefined) {
			return null;
		}

		if (typeof selection === 'object' && Object.prototype.hasOwnProperty.call(selection, 'value')) {
			return (selection as Record<string, unknown>)['value'] as T;
		}

		return selection as T;
	}

	dropdownPrazoPaymentSomeMonths() {
		this.dropdownPrazoPayment = [
			{ label: 'TRANSLATE.MONTH.ONE', value: 1 },
			{ label: 'TRANSLATE.MONTH.TWO', value: 2 },
			{ label: 'TRANSLATE.MONTH.THREE', value: 3 },
			{ label: 'TRANSLATE.MONTH.FOUR', value: 4 },
			{ label: 'TRANSLATE.MONTH.FIVE', value: 5 },
			{ label: 'TRANSLATE.MONTH.SIX', value: 6 },
			{ label: 'TRANSLATE.MONTH.SEVEN', value: 7 },
			{ label: 'TRANSLATE.MONTH.EIGHT', value: 8 },
			{ label: 'TRANSLATE.MONTH.NINE', value: 9 },
			{ label: 'TRANSLATE.MONTH.TEN', value: 10 },
			{ label: 'TRANSLATE.MONTH.ELEVEN', value: 11 },
			{ label: 'TRANSLATE.MONTH.TWELVE', value: 12 },
			{ label: 'TRANSLATE.MONTH.EIGHTEEN', value: 18 },
			{ label: 'TRANSLATE.MONTH.TWENTY.FOUR', value: 24 },
			{ label: 'TRANSLATE.MONTH.THIRTY.SIX', value: 36 },
			{ label: 'TRANSLATE.MONTH.FORTY.EIGHT', value: 48 },
			{ label: 'TRANSLATE.MONTH.SIXTY', value: 60 }
		];
	}

	dropdownPrazoPaymentOneMonth() {
		this.dropdownPrazoPayment = [
			{ label: 'TRANSLATE.NOT.APPLICABLE', value: 1 }
		];
	}

	dropdownPrazoPaymentSelecione() {
		this.dropdownPrazoPayment = [];
	}

	pt = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
		dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
		dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
		monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
			'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
		monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
		today: 'Hoje',
		clear: 'Limpar'
	};

	es = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sábado'],
		dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
		dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
		monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
			'Agosto', 'Septiembre', 'Octubre', 'Noviembro', 'Diciembro'],
		monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
		today: 'Hoy',
		clear: 'Limpar'
	};

	rangeDateLabel(): string {
		const date = new Date();
		const dateLabelYear = date.getFullYear();
		return '1900:' + dateLabelYear;
	}

	isValidCPF(value: string): boolean {

		if (typeof value !== 'string') {
			return false;
		}

		value = value.replace(/[^\d]+/g, '');

		if (value.length !== 11 || !!value.match(/(\d)\1{10}/)) {
			return false;
		}

		const values = value.split('').map(el => +el);
		// const rest = (count) => (values.slice(0, count - 12).reduce((soma, el, index) => (soma + el * (count - index)), 0) * 10) % 11 % 10;
		const rest = (count: number) =>
			(values.slice(0, count - 12).reduce((soma: number, el: number, index: number) =>
				(soma + el * (count - index)), 0) * 10) % 11 % 10;

		return rest(10) === values[9] && rest(11) === values[10];
	}

	contractStartDate(): Date {
		let date = new Date();
		if (this.vinValidate.warrantyStartDate !== null) {
			date = this.vinValidate.warrantyStartDate;
		}
		return date;
	}

	enforceNumericPhoneInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const numericValue = (input.value || '').replace(/\D+/g, '');

		if (input.value !== numericValue) {
			input.value = numericValue;
		}

		this.userform.get('inputPhoneSeller')?.setValue(numericValue, { emitEvent: false });
		this.userform.get('inputPhoneSeller')?.updateValueAndValidity({ emitEvent: false });
	}

	allowOnlyDigits(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

		if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
			return;
		}

		if (!/^[0-9]$/.test(event.key)) {
			event.preventDefault();
		}
	}

	handlePhonePaste(event: ClipboardEvent): void {
		const pastedText = event.clipboardData?.getData('text') || '';
		if (/\D/.test(pastedText)) {
			event.preventDefault();
		}
	}

	private markControlForValidation(controlName: string): void {
		const control = this.userform.get(controlName);
		if (!control) {
			return;
		}

		control.markAsDirty();
		control.markAsTouched();
		control.markAsPending();
	}

	private focusControlByFormName(controlName: string): boolean {
		const focusElement = (element: HTMLElement | null): boolean => {
			if (!element) {
				return false;
			}

			element.scrollIntoView({ behavior: 'smooth', block: 'center' });
			element.focus();
			setTimeout(() => element.focus(), 60);
			return true;
		};

		const directTargetMap: Record<string, string> = {
			dealerCodigo: '#dealerCodigo',
			dealerEmail: '#dealerEmail',
			cdsidSeller: 'input[formcontrolname="cdsidSeller"]',
			nameSeller: 'input[formcontrolname="nameSeller"]',
			inputPhoneSeller: '#phoneSeller',
			emailSeller: 'input[formcontrolname="emailSeller"]',
			typePayment: 'p-dropdown[formcontrolname="typePayment"] .p-dropdown',
			formaPayment: 'p-dropdown[formcontrolname="formaPayment"] .p-dropdown',
			prazoPayment: 'p-dropdown[formcontrolname="prazoPayment"] .p-dropdown'
		};

		const directTarget = directTargetMap[controlName]
			? document.querySelector(directTargetMap[controlName]) as HTMLElement | null
			: null;

		if (focusElement(directTarget)) {
			return true;
		}

		const hostElement = document.querySelector(`[formcontrolname="${controlName}"]`) as HTMLElement | null;
		if (!hostElement) {
			return false;
		}

		const focusTarget = (hostElement.matches('input, textarea, select, button, [tabindex]')
			? hostElement
			: hostElement.querySelector('input, textarea, select, button, [tabindex]')) as HTMLElement | null;

		return focusElement(focusTarget);
	}

	private focusFirstInvalidRequiredField(): void {
		const orderedControls = [
			'dealerCodigo',
			'dealerEmail',
			'cdsidSeller',
			'nameSeller',
			'inputPhoneSeller',
			'emailSeller',
			'typePayment',
			'formaPayment',
			'prazoPayment'
		];

		for (const controlName of orderedControls) {
			const control = this.userform.get(controlName);
			if ((control?.invalid || control?.pending) && this.focusControlByFormName(controlName)) {
				return;
			}
		}
	}

	getSummaryDocumentValue(documentValue: string): string {
		if (this.systemCountry === ConstantsFordProtect.BRA) {
			return maskCpfAndCnpj(documentValue);
		}

		return (documentValue || '').trim();
	}

	getDocumentLabel(cpfCnpj: string): string {
		
		const documentLabelsByCountry: Partial<Record<string, string>> = {
			[ConstantsFordProtect.ARG]: 'DNI/CUIT:',
			[ConstantsFordProtect.CHL]: 'RUN/RUT:',
			[ConstantsFordProtect.COL]: 'Cédula/NIT:',
			[ConstantsFordProtect.VEN]: 'Cédula/RIF:'
		};

		const countryLabel = documentLabelsByCountry[this.systemCountry];
		if (countryLabel) {
			return countryLabel;
		}

		const nationalIdDigitsLength = this.getNationalIdDigits(cpfCnpj).length;

		if (nationalIdDigitsLength === 11) {
			return 'CPF:';
		}

		if (nationalIdDigitsLength === 14) {
			return 'CNPJ:';
		}

		return 'CPF/CNPJ:';
	}

	private getNationalIdDigits(cpfCnpj: string): string {
		return (cpfCnpj || '').replace(/\D/g, '');
	}

	buttonNext() {
		this.submitted = true;

		if (this.blockedUi) {
			return;
		}

		this.syncContractSummaryFromForm();

		const typePaymentControl = this.userform.get('typePayment');
		this.paymentType = this.getDropdownValue<boolean>(typePaymentControl?.value);

		const paymentMethodControl = this.userform.get('formaPayment');
		this.paymentMethod = this.getDropdownValue<string>(paymentMethodControl?.value);

		const paymentTermControl = this.userform.get('prazoPayment');
		this.paymentTerm = this.getDropdownValue<number>(paymentTermControl?.value);

		const sellerPhoneValue = this.userform.get('inputPhoneSeller')?.value || '';

		const dealerCodigoControl = this.userform.get('dealerCodigo');
		const cdsidSellerControl = this.userform.get('cdsidSeller');
		const nameSellerControl = this.userform.get('nameSeller');
		const inputPhoneSellerControl = this.userform.get('inputPhoneSeller');
		const emailSellerControl = this.userform.get('emailSeller');
		const dealerEmailControl = this.userform.get('dealerEmail');
		const typePaymentFormControl = this.userform.get('typePayment');
		const formaPaymentControl = this.userform.get('formaPayment');
		const prazoPaymentControl = this.userform.get('prazoPayment');
		let hasValidationError = false;

		if (dealerCodigoControl?.invalid) {
			this.markControlForValidation('dealerCodigo');
			hasValidationError = true;
		}
		if (cdsidSellerControl?.invalid) {
			this.markControlForValidation('cdsidSeller');
			hasValidationError = true;
		}
		if (nameSellerControl?.invalid) {
			this.markControlForValidation('nameSeller');
			hasValidationError = true;
		}
		if (inputPhoneSellerControl?.invalid || sellerPhoneValue.length <= 0) {

			this.markControlForValidation('inputPhoneSeller');
			hasValidationError = true;
		}
		if (emailSellerControl?.invalid) {
			this.markControlForValidation('emailSeller');
			hasValidationError = true;
		}
		if (dealerEmailControl?.invalid) {
			this.markControlForValidation('dealerEmail');
			hasValidationError = true;
		}

		if (!this.isDealerCodeValid) {
			this.markControlForValidation('dealerCodigo');
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.DEALER.NOT.FOUND.ACTION')
			});
			this.focusControlByFormName('dealerCodigo');
			return;
		}

		if (typePaymentFormControl?.invalid || this.paymentType === null || this.paymentType === undefined) {

			this.markControlForValidation('typePayment');
			hasValidationError = true;
		}
		if (formaPaymentControl?.invalid || this.paymentMethod === null || this.paymentMethod === undefined) {

			this.markControlForValidation('formaPayment');
			hasValidationError = true;
		}
		if (prazoPaymentControl?.invalid || this.paymentTerm === null || this.paymentTerm === undefined) {

			this.markControlForValidation('prazoPayment');
			hasValidationError = true;
		}

		if (hasValidationError || this.userform.invalid) {
			this.focusFirstInvalidRequiredField();
			return;
		}

		this.contractSummary.dealerSeller.phone = inputPhoneSellerControl?.value || '';

		switch (this.systemCountry) {

			case ConstantsFordProtect.ARG:

				this.contractSummaryService.confirmDealerData();
				this.contractSummaryService.setContractSummary(this.contractSummary);

				if (this.choosePlanBuyng.maintenance?.productId && this.choosePlanBuyng.maintenance.productId > 0) {

					this.router.navigate(
						['legal-text-contract-maintenance-argentina', this.saleChannel],
						{ skipLocationChange: true, replaceUrl: false }
					);
				} else {

					this.router.navigate(['legal-text-contract-argentina', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
				}
				break;

			case ConstantsFordProtect.BRA:

				this.contractSummaryService.confirmDealerData();
				this.contractSummaryService.setContractSummary(this.contractSummary);

				if ((this.choosePlanBuyng.maintenance?.productId && this.choosePlanBuyng.maintenance.productId > 0)
					|| (this.choosePlanBuyng.part?.productId && this.choosePlanBuyng.part.productId > 0)) {

					this.router.navigate(
						['legal-text-contract-bra-dealer-reviews', this.saleChannel],
						{ skipLocationChange: true, replaceUrl: false }
					);
				} else {

					if (this.contractSummary.typePayment?.value === true) {

						this.router.navigate(['contract-summary-attachment-one',
							this.saleChannel], { skipLocationChange: true, replaceUrl: false });
					} else if (this.contractSummary.typePayment?.value === false) {

						this.router.navigate(['contract-summary-attachment-two',
							this.saleChannel], { skipLocationChange: true, replaceUrl: false });
					}
				}

				break;

			default:
				this.save();
				break;
		}
	}

	save(): void {

		this.blockedUi = true;

		this.contractSummaryService.saveContractPendingSignature(this.setContractPendingSignature())
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: ContractPendingSignature) => {
				this.messageService.add({
					severity: 'success',
					summary: this.translateService.instant('TRANSLATE.SUCCESS'),
					detail: this.translateService.instant('TRANSLATE.CONTRACT.CREATED.SUCCESSFULL')
				});
				this.vinValidate = new VinValidate();
				this.choosePlanBuyng = new ChoosePlanBuyng();
				this.clientData = new ClientData();
				this.contractSummary = new ContractSummary();

				this.contractIdDownload = response.contractId;
				this.isContractCreated = true;
				this.blockedUi = false;
				this.lang = "";
				this.router.navigate(['home']);

			}, (err) => {

				if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else if (err.status === 400 || err.status === 404 || err.status === 500) {
					// Attempt to map backend error to specific i18n key
					const mappedError = this.validationErrorMapperService.mapBackendErrorToI18nKey(err);
					const errorKey = mappedError.key.startsWith('TRANSLATE.ERROR.CEP.')
						? 'TRANSLATE.CONTRACT.CANNOT.BE.SAVE.CONTACT.ADM'
						: mappedError.key;
					const errorDetail = this.translateService.instant(errorKey);

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: errorDetail
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.blockedUi = false;
				this.isContractCreated = false;
			});
	}


	private setContractPendingSignature(): ContractPendingSignature {
		const contractPendingSignature = new ContractPendingSignature();
		const formaPaymentValue = this.getDropdownValue<string>(this.contractSummary?.formaPayment);
		const prazoPaymentValue = this.getDropdownValue<number>(this.contractSummary?.prazoPayment);
		const dealer = this.contractSummary.dealer;
		const dealerSeller = this.contractSummary.dealerSeller;
		const client = this.clientData;
		const extension = this.choosePlanBuyng.extension;
		const maintenance = this.choosePlanBuyng.maintenance;
		const part = this.choosePlanBuyng.part;
		const sellerPhone = dealerSeller?.phone || '';
		const customerZip = this.systemCountry !== ConstantsFordProtect.BRA && (!client?.zip || client.zip === '')
			? this.doubleZero
			: (client?.zip || '');

		contractPendingSignature.country = this.systemCountry;
		contractPendingSignature.operation = OperationCode.CAR;
		contractPendingSignature.status = StatusTypeEnum.FILES;
		contractPendingSignature.user = ConstantsFordProtect.EMISSION_USER;
		contractPendingSignature.saleDate = new Date();
		contractPendingSignature.startDate = this.contractStartDate();
		contractPendingSignature.saleType = SalesTypeFordEnum.RETAIL_CODE;
		contractPendingSignature.finalPrice = this.choosePlanBuyng.totalPrice || 0;

		if (this.vinValidate.odometro !== null && this.vinValidate.odometro !== undefined) {
			contractPendingSignature.odometerStart = this.vinValidate.odometro;
		} else {
			contractPendingSignature.odometerStart = 1;
		}

		contractPendingSignature.vin.vin = this.vinValidate.vin || '';
		contractPendingSignature.vin.cvvgDate = this.contractStartDate();
		contractPendingSignature.vin.factoryWarrantyEndDate = null;

		contractPendingSignature.vin.idVinCategoryVersion = this.vinValidate.idVinCategoryVersion;

		contractPendingSignature.payments.method = "DEALER";
		contractPendingSignature.payments.modality = formaPaymentValue ?? '';
		contractPendingSignature.payments.parcels = prazoPaymentValue ?? 0;
		contractPendingSignature.payments.totalValue = this.choosePlanBuyng.totalPrice || 0;

		contractPendingSignature.dealers.code = dealer?.dealerCode || '';
		contractPendingSignature.dealers.pAndAC = dealer?.pandAC || '';
		contractPendingSignature.dealers.divisionCode = dealer?.divisionCode || '';
		contractPendingSignature.dealers.oldRegionCode = dealer?.oldRegionCode || '';
		contractPendingSignature.dealers.geoSalesCode = dealer?.geoSalesCode || '';
		contractPendingSignature.dealers.subCode = dealer?.dealerSubCode || '';
		contractPendingSignature.dealers.name = dealer?.dealerName || '';
		contractPendingSignature.dealers.email = dealer?.dealerEmail || '';
		contractPendingSignature.dealers.phone = dealer?.dealerPhone || '';

		contractPendingSignature.sellers.code = dealerSeller?.sellerCode || '';
		contractPendingSignature.sellers.name = dealerSeller?.name || '';

		if (this.systemCountry === ConstantsFordProtect.ARG &&
			(sellerPhone.length === 0 || sellerPhone === ConstantsFordProtect.EMPTY_PHONE_ARGENTINA)) {

			contractPendingSignature.sellers.phone = ConstantsFordProtect.EMPTY_PHONE_ARGENTINA;
		} else {

			contractPendingSignature.sellers.phone = sellerPhone;
		}
		contractPendingSignature.sellers.email = dealerSeller?.email || '';
		contractPendingSignature.sellers.document = dealerSeller?.document || '';
		contractPendingSignature.sellers.birthday = dealerSeller?.birthday;

		contractPendingSignature.customers.country = client?.countryCode || '';
		contractPendingSignature.customers.documentType =  nationalId(this.clientData.nationalId, this.systemCountry);
		contractPendingSignature.customers.documentNumber = client?.nationalId || '';
		contractPendingSignature.customers.name = client?.firstname || '';
		contractPendingSignature.customers.phone = client?.phone1 || '';
		contractPendingSignature.customers.email = client?.email1 || '';
		contractPendingSignature.customers.lastName = client?.lastname || '';
		contractPendingSignature.customers.customerSocialName = client?.customerSocialName || '';
		contractPendingSignature.customers.cep = customerZip;
		contractPendingSignature.customers.streetName = client?.address || '';
		contractPendingSignature.customers.streetNumber = client?.number || '';
		contractPendingSignature.customers.streetComplement = client?.complement || '';
		contractPendingSignature.customers.district = client?.district || '';
		contractPendingSignature.customers.state = client?.stateCode || '';
		contractPendingSignature.customers.city = client?.city || '';

		if ((extension?.coverageCategoryId || 0) > 0 && (extension?.productId || 0) > 0) {
			const productFullExtension = new ProductFull();
			productFullExtension.coverageId = extension?.coverageCategoryId || 0;
			productFullExtension.productId = extension?.productId || 0;
			productFullExtension.productPolicyId = extension?.productPolicyKey || 0;
			productFullExtension.priceBookId = extension?.pricebookKey || 0;
			contractPendingSignature.products.push(productFullExtension);
		}

		if ((maintenance?.coverageCategoryId || 0) > 0 && (maintenance?.productId || 0) > 0) {
			const productFullMaintenance = new ProductFull();
			productFullMaintenance.coverageId = maintenance?.coverageCategoryId || 0;
			productFullMaintenance.productId = maintenance?.productId || 0;
			productFullMaintenance.productPolicyId = maintenance?.productPolicyKey || 0;
			productFullMaintenance.priceBookId = maintenance?.pricebookKey || 0;
			contractPendingSignature.products.push(productFullMaintenance);
		}

		if ((part?.coverageCategoryId || 0) > 0 && (part?.productId || 0) > 0) {
			const productFullPart = new ProductFull();
			productFullPart.coverageId = part?.coverageCategoryId || 0;
			productFullPart.productId = part?.productId || 0;
			productFullPart.productPolicyId = part?.productPolicyKey || 0;
			productFullPart.priceBookId = part?.pricebookKey || 0;
			contractPendingSignature.products.push(productFullPart);
		}

		return contractPendingSignature;
	}

}

